import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface StandingsCacheData {
  league_id: string;
  season: number;
  data_json: any;
  updated_at: string;
}

export interface StandingEntry {
  rank: number;
  team: {
    id: number;
    name: string;
    logo: string;
  };
  points: number;
  goalsDiff: number;
  group: string;
  form: string;
  status: string;
  description: string;
  all: {
    played: number;
    win: number;
    draw: number;
    lose: number;
    goals: {
      for: number;
      against: number;
    };
  };
}

interface UseStandingsCacheOptions {
  leagueApiId?: number;
  season?: number;
}

interface StandingsResult {
  standings: StandingEntry[];
  lastUpdated: string | null;
  leagueInfo: any;
}

async function fetchStandingsData(
  leagueApiId: number,
  season: number
): Promise<StandingsResult> {
  // First get the league UUID from api_football_id
  const { data: leagueData, error: leagueError } = await supabase
    .from('leagues')
    .select('id, name, country, logo_url, api_football_id')
    .eq('api_football_id', leagueApiId)
    .single();

  if (leagueError) {
    console.error('Error fetching league:', leagueError);
    throw leagueError;
  }

  if (!leagueData) {
    console.warn('League not found for api_football_id:', leagueApiId);
    return { standings: [], lastUpdated: null, leagueInfo: null };
  }

  // Now fetch standings cache
  const { data, error } = await supabase
    .from('standings_cache')
    .select('*')
    .eq('league_id', leagueData.id)
    .eq('season', season)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching standings cache:', error);
    throw error;
  }

  if (data?.data_json && typeof data.data_json === 'object') {
    const jsonData = data.data_json as any;
    if (jsonData?.response?.[0]?.league?.standings?.[0]) {
      return {
        standings: jsonData.response[0].league.standings[0],
        lastUpdated: data.updated_at,
        leagueInfo: leagueData
      };
    }
  }

  return { standings: [], lastUpdated: null, leagueInfo: leagueData };
}

export function useStandingsCache(options: UseStandingsCacheOptions = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { leagueApiId, season = new Date().getFullYear() } = options;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['standings-cache', leagueApiId, season],
    queryFn: () => fetchStandingsData(leagueApiId!, season),
    enabled: !!leagueApiId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  // Show error toast
  if (error) {
    toast({
      title: "Erro",
      description: "Erro ao carregar tabela classificativa",
      variant: "destructive"
    });
  }

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['standings-cache', leagueApiId, season] });
  };

  return {
    standings: data?.standings ?? [],
    loading: isLoading,
    lastUpdated: data?.lastUpdated ?? null,
    leagueInfo: data?.leagueInfo ?? null,
    refresh,
    refetch
  };
}
